/**
 * Questionnaires — /w/:slug/app/questionnaires (list view).
 *
 * P8 / M1 list surface, wired to lib/api.ts (PR-P2-2). Reads
 * GET /api/w/:slug/questionnaires and renders a table of the
 * workspace's questionnaires with status/deadline chips, a `+ New`
 * action, and filter chips.
 *
 * PR-P2-2 additions — strand recovery + filter chips (UC-5 R1 decision,
 * .claude/skills/ux-adversary/runs/uc-5-questionnaires/design-rationale.md):
 * an upload whose XLSX column mapping was never confirmed shows as a
 * "Needs mapping" strand row with a Resume mapping → action, instead of
 * a dead-end that told the user to delete and re-upload (the R1
 * "current-code foot-gun" the mockup was built to fix).
 *
 * Reduced scope vs the R2 mockup (questionnaires-list.web.html): the
 * hero glance-strip (Active/In review/Sent/Due<7d tiles), the
 * 4-segment pipeline stepper, the multi-assignee avatar stack, and the
 * strand row's Discard button + 30-day auto-discard are NOT built here.
 * The stepper/avatars need a per-question assignee model that doesn't
 * exist; Discard needs a DELETE endpoint (none exists — deleting a
 * compliance record needs a deliberate audit-trail decision, not an
 * ad-hoc route) plus a cron for auto-discard. Filter chips (this PR's
 * actual scope line) + the strand row's core "never delete-and-reupload"
 * guarantee (this PR's actual acceptance bar) are what's built. See
 * docs/implementation-plan.md §3.2 deviation notes / Appendix F.
 *
 * The detail view lands in a later phase; this page shows kind, status,
 * deadline, and created_at, and lets the user create a new paste or
 * upload questionnaire.
 *
 * ux-adversary critique pass (2026-08-27, Marcus + Nadia persona critics
 * + aesthetic + flow critics) fixed in this PR: the strand row's warning
 * text used `var(--band-amber-fg, ...)` — a token that doesn't exist
 * anywhere in the shipped design system, so it silently rendered as
 * plain muted gray instead of amber (aesthetic High, independently
 * verified against `dist/tokens.css` and `direction-b.css` before
 * fixing — now uses the verified `--warning-fg`/`--warning-bg` pair
 * `DeadlineChip` already used); rows now sort by deadline urgency
 * (Nadia High — a "what's on fire" queue sorted by upload time instead
 * of urgency defeats its own purpose); the workspace name is now in the
 * subtitle (Nadia Medium — easy to act on the wrong client's tenant
 * when tabs look identical); Kind is humanized (Nadia Nit); the
 * redundant duplicate "Open →" link is dropped for non-strand rows
 * (Nadia Nit — title is already the click target); the strand row's
 * Resume button is `secondary` not `primary` (aesthetic Medium — it was
 * competing with the page header's actual primary CTA); "Abandoned"
 * softened to less judgmental wording (Marcus Nit); the table now
 * scrolls horizontally instead of clipping on narrow viewports (Marcus
 * Medium). See docs/implementation-plan.md §3.2 deviation notes for
 * what was fixed vs deferred.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import type { DeadlineUrgency, QuestionnaireKind, QuestionnaireStatus } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { listQuestionnaires, type QuestionnaireListRow, type QuestionnaireEvidenceCounts } from '../lib/api';

const NOTICE_COPY: Record<string, string> = {
  'strand-saved': 'Saved — the upload stays in your queue as "Needs mapping" until you finish confirming columns.',
  created: 'Questionnaire created.',
  extracted: 'Mapping confirmed — extraction started.',
};

// Urgency floats to the top of the list — a triage queue sorted by
// upload time instead of "what's on fire" defeats its own purpose
// (ux-adversary persona:Nadia, High).
const URGENCY_RANK: Record<DeadlineUrgency, number> = {
  abandoned: 0,
  urgent: 1,
  soon: 2,
  future: 3,
};

const KIND_LABEL: Record<QuestionnaireKind, string> = {
  paste: 'Pasted',
  upload: 'Upload',
  template: 'Template',
};

const STATUS_LABEL: Record<QuestionnaireStatus, string> = {
  extracting: 'Extracting',
  matching: 'Matching',
  drafting: 'Drafting',
  in_review: 'In review',
  approved: 'Approved',
  exported: 'Exported',
};

// Badge only supports `neutral` | `accent`. Terminal states get `accent`;
// in-flight pipeline stages stay `neutral`. Richer status-severity mapping
// is an M5 review-UI concern, not an M1 list-view one.
const STATUS_TONE: Record<QuestionnaireStatus, 'neutral' | 'accent'> = {
  extracting: 'neutral',
  matching: 'neutral',
  drafting: 'neutral',
  in_review: 'neutral',
  approved: 'accent',
  exported: 'accent',
};

type FilterBucket = 'all' | 'active' | 'in_review' | 'sent' | 'needs_mapping';

const FILTERS: Array<{ value: FilterBucket; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'in_review', label: 'In review' },
  { value: 'sent', label: 'Sent' },
  { value: 'needs_mapping', label: 'Needs mapping' },
];
const FILTER_VALUES = FILTERS.map((f) => f.value);
function isFilterBucket(v: string | null): v is FilterBucket {
  return v !== null && (FILTER_VALUES as string[]).includes(v);
}

/** An upload whose XLSX column mapping was never confirmed — the row
 *  persists (nothing was lost) but extraction can't start until the
 *  user resumes the mapping step. Paste-kind rows extract inline
 *  within their creation request, so they don't produce this state. */
function isStrand(q: QuestionnaireListRow): boolean {
  return q.kind === 'upload' && q.status === 'extracting' && !q.xlsx_mapping_json;
}

function bucketFor(q: QuestionnaireListRow): FilterBucket {
  if (isStrand(q)) return 'needs_mapping';
  if (q.status === 'in_review') return 'in_review';
  if (q.status === 'approved' || q.status === 'exported') return 'sent';
  return 'active';
}

export function Questionnaires() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const newHref = `/w/${slug}/app/questionnaires/new`;

  const { data: stateData, error: stateError, loading: stateLoading } = useWorkspaceQuery(
    (slug) => queryKeys.questionnaires(slug),
    (slug) => listQuestionnaires(slug),
  );
  const state = { data: stateData, error: stateError, loading: stateLoading };
  const rows = state.data?.questionnaires ?? [];

  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get('filter');
  const filter: FilterBucket = isFilterBucket(rawFilter) ? rawFilter : 'all';
  const setFilter = useCallback(
    (next: FilterBucket) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'all') params.delete('filter');
          else params.set('filter', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // One-shot flash notice from QuestionnaireNew.tsx (?notice=strand-saved
  // etc.) — read once, then strip so a refresh doesn't re-show it.
  const notice = searchParams.get('notice');
  const noticeText = notice ? NOTICE_COPY[notice] : null;
  useEffect(() => {
    if (!notice) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('notice');
        return params;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(() => {
    const c: Record<FilterBucket, number> = { all: rows.length, active: 0, in_review: 0, sent: 0, needs_mapping: 0 };
    for (const q of rows) c[bucketFor(q)] += 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const list = filter === 'all' ? rows : rows.filter((q) => bucketFor(q) === filter);
    return [...list].sort((a, b) => {
      // Strands first (nothing extracts until someone acts), then by
      // urgency, then newest first within the same urgency.
      const strandDiff = Number(isStrand(b)) - Number(isStrand(a));
      if (strandDiff !== 0) return strandDiff;
      const rankA = a.deadline_urgency ? URGENCY_RANK[a.deadline_urgency] : 99;
      const rankB = b.deadline_urgency ? URGENCY_RANK[b.deadline_urgency] : 99;
      if (rankA !== rankB) return rankA - rankB;
      return b.created_at.localeCompare(a.created_at);
    });
  }, [rows, filter]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <PageHeader
        title="Questionnaires"
        subtitle={`${workspace.name} · Paste a questionnaire → cited draft.`}
        right={
          <Link to={newHref} style={{ textDecoration: 'none' }}>
            <Button variant="primary">+ New questionnaire</Button>
          </Link>
        }
      />

      {noticeText ? <Alert variant="success">{noticeText}</Alert> : null}
      {state.loading ? <Spinner /> : null}
      {state.error ? <Alert variant="error">{state.error}</Alert> : null}

      {!state.loading && rows.length === 0 ? (
        <EmptyState
          title="No questionnaires yet."
          description="Paste your first vendor security questionnaire, or upload a CAIQ/SIG XLSX."
          action={
            <Link to={newHref} style={{ textDecoration: 'none' }}>
              <Button variant="primary">+ New questionnaire</Button>
            </Link>
          }
        />
      ) : null}

      {rows.length > 0 ? (
        <>
          <div role="radiogroup" aria-label="Filter by status" style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {FILTERS.map((f) => {
              const on = f.value === filter;
              return (
                <button
                  key={f.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setFilter(f.value)}
                  style={{
                    appearance: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '6px var(--space-3)',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent)' : 'transparent',
                    color: on ? 'var(--text-inverse)' : 'var(--text-muted)',
                    borderRadius: '999px',
                    fontSize: 'var(--font-size-3xs)',
                    fontWeight: on ? 700 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {f.label}
                  <span style={{ opacity: 0.8 }}>{counts[f.value]}</span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <EmptyState title="No questionnaires in this filter." description="Try a different filter, or clear it to see everything." />
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-elevated, var(--surface))', textAlign: 'left' }}>
                      <th style={th}>Title</th>
                      <th style={th}>Kind</th>
                      <th style={th}>Status</th>
                      <th style={th}>Evidence</th>
                      <th style={th}>Deadline</th>
                      <th style={th}>Created</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((q) =>
                      isStrand(q) ? (
                        <StrandRow key={q.questionnaire_id} q={q} slug={slug} />
                      ) : (
                        <tr key={q.questionnaire_id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={td}>
                            <Link
                              to={`/w/${slug}/app/questionnaires/${encodeURIComponent(q.questionnaire_id)}`}
                              style={{ color: 'var(--text)', fontWeight: 500, textDecoration: 'none' }}
                            >
                              {q.title}
                            </Link>
                            {q.source_filename ? (
                              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', marginLeft: 'var(--space-2)' }}>
                                {q.source_filename}
                              </span>
                            ) : null}
                          </td>
                          <td style={td}>
                            <span style={{ color: 'var(--text-muted)' }}>{KIND_LABEL[q.kind]}</span>
                          </td>
                          <td style={td}>
                            <Badge tone={STATUS_TONE[q.status]}>{STATUS_LABEL[q.status]}</Badge>
                          </td>
                          <td style={td}>
                            <EvidenceChip counts={q.evidence_counts} />
                          </td>
                          <td style={td}>
                            <DeadlineChip urgency={q.deadline_urgency} dueDate={q.due_date} />
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)' }}>{formatDate(q.created_at)}</td>
                          <td style={td} />
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ---- Strand row (UC-5 R1 orphan-recovery pattern) -----------------------

function StrandRow({ q, slug }: { q: QuestionnaireListRow; slug: string }) {
  return (
    // Amber pairing matches DeadlineChip's verified-existing tokens
    // (--warning-bg/--warning-fg) rather than --band-amber-fg, which
    // doesn't exist anywhere in the shipped design system (ux-adversary
    // aesthetic critic, High — confirmed against dist/tokens.css and
    // direction-b.css before fixing: the old value silently fell back
    // to plain text-muted, so the one row meant to demand attention
    // rendered as if it didn't need any).
    <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--warning-bg)' }}>
      <td style={td}>
        <span style={{ fontWeight: 500, color: 'var(--text)' }}>{q.title}</span>
        {q.source_filename ? (
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', marginLeft: 'var(--space-2)' }}>
            {q.source_filename}
          </span>
        ) : null}
      </td>
      <td style={td} colSpan={3}>
        <span
          role="status"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            color: 'var(--warning-fg)',
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          ⚠ Mapping incomplete — pick which columns hold the question text before we can extract.
        </span>
      </td>
      <td style={{ ...td, color: 'var(--text-muted)' }}>{formatDate(q.created_at)}</td>
      <td style={td}>
        {/* Secondary, not primary — a table row shouldn't emit a
            CTA-weight button that competes with the page header's
            actual primary action (ux-adversary aesthetic, Medium). The
            amber row background already carries the urgency signal. */}
        <Link
          to={`/w/${slug}/app/questionnaires/new?resume=${encodeURIComponent(q.questionnaire_id)}`}
          style={{ textDecoration: 'none' }}
        >
          <Button variant="secondary">Resume mapping →</Button>
        </Link>
      </td>
    </tr>
  );
}

// ---- Deadline chip --------------------------------------------------

function DeadlineChip({ urgency, dueDate }: { urgency: DeadlineUrgency | null; dueDate: string | null }) {
  if (!urgency || !dueDate) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
  const tone: Record<DeadlineUrgency, { bg: string; fg: string }> = {
    future: { bg: 'var(--surface-alt, var(--surface))', fg: 'var(--text-muted)' },
    soon: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' },
    urgent: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' },
    abandoned: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' },
  };
  const t = tone[urgency];
  // "Abandoned" reads as the tool judging the user; a past-due date in
  // the same amber tone says the same thing without editorializing
  // (ux-adversary persona:Marcus, Nit).
  const label = urgency === 'abandoned' ? `Past due · ${formatDate(dueDate)}` : formatDate(dueDate);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {label}
    </span>
  );
}

const th: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  fontSize: 'var(--font-size-3xs)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-dim)',
  fontWeight: 600,
};

const td: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  fontSize: 'var(--font-size-2xs)',
  verticalAlign: 'top',
};

function EvidenceChip({ counts }: { counts: QuestionnaireEvidenceCounts | null }) {
  if (!counts || counts.total === 0) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
  const pct = Math.round((counts.attested / counts.total) * 100);
  const tone = pct >= 80
    ? { bg: 'var(--success-bg)', fg: 'var(--success-fg)' }
    : pct >= 30
      ? { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' }
      : { bg: 'var(--surface-alt, var(--surface))', fg: 'var(--text-muted)' };
  return (
    <span
      title={`${counts.attested} evidence-backed, ${counts.declared} partial, ${counts.ai_inferred} aspirational, ${counts.no_match} no match`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        background: tone.bg,
        color: tone.fg,
        cursor: 'help',
      }}
    >
      {pct}%
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
